import type { ReactNode } from 'react';
import { ApiError } from '@/api/client';
import { plainError, refusalTitle } from '@/ops/errorMessages';

const SERVICE_LABELS: Record<string, string> = {
  billing: 'Billing',
  messaging: 'Messaging',
  iam: 'IAM',
  ai: 'AI',
  salesiq: 'SalesIQ',
  metadata: 'Metadata',
  settings: 'Settings',
};

/** Human wording for the panel's `reason` (docs/api-tenant-ops.md §0). */
const REASONS: Record<string, string> = {
  timeout: 'the service did not answer in time',
  unreachable: 'the service could not be reached from the console',
  http_5xx: 'the service answered with a server error',
  unknown_environment: 'the environment is not in the registry',
  environment_disabled: 'the environment is disabled',
  key_missing: 'no internal API key is set for this environment',
  key_unreadable: 'the stored internal API key cannot be decrypted (key ring)',
  service_not_configured: 'this service has no URL in the environment',
};

export function serviceLabel(service: string | null | undefined): string {
  if (!service) return 'Service';
  return SERVICE_LABELS[service] ?? service.charAt(0).toUpperCase() + service.slice(1);
}

export function describeReason(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return REASONS[reason] ?? reason.replace(/_/g, ' ');
}

interface KnownUpstreamCode {
  title: string;
  message: string;
  /** False when the service is up and simply has nothing for this tenant — not an outage. */
  outage: boolean;
}

/**
 * A service's own code for its 503 (`upstream_code`), in words. The service is answering; it is telling us something
 * specific that "unavailable" would hide.
 */
const UPSTREAM_CODES: Record<string, KnownUpstreamCode> = {
  MESSAGING_TENANT_NOT_PROVISIONED: {
    title: 'No messaging account yet',
    message:
      'This tenant has no SMS and WhatsApp account. Tenants created before messaging existed, or whose '
      + '"Set up SMS and WhatsApp" step has not run, have none.',
    outage: false,
  },
  AI_WALLET_NOT_PROVISIONED: {
    title: 'No AI wallet yet',
    message: 'This tenant has no AI credit wallet. Its "Set up AI" step has not run.',
    outage: false,
  },
  SALESIQ_PROVISION_FAILED: {
    title: 'Live chat setup failed',
    message: 'SalesIQ could not finish preparing this tenant\'s database. Retry the step; if it fails again, an engineer must check the SalesIQ logs.',
    outage: true,
  },
};

export function describeUpstreamCode(code: string | null | undefined): KnownUpstreamCode | null {
  return code ? UPSTREAM_CODES[code] ?? null : null;
}

interface UnavailablePanelProps {
  /** The 503 answer; its problem body names the service, environment and reason. */
  error?: ApiError | null;
  /** Fallback label when the body does not name the service. */
  service?: string;
  /** For a failed WRITE: the outcome is unknown, so say that instead of "not shown". */
  write?: boolean;
  /** Extra fields for a panel built from a products `LivePanel` rather than an ApiError. */
  reason?: string | null;
  environment?: string | null;
  upstreamStatus?: number | null;
  upstreamCode?: string | null;
  detail?: string | null;
}

/**
 * The labelled 503 panel (CP2): a live figure that is not there is SAID to be not there — never replaced
 * by a cached value, a replica copy or a zero.
 */
export function UnavailablePanel({
  error, service, write = false, reason, environment, upstreamStatus, upstreamCode, detail,
}: UnavailablePanelProps) {
  const problem = error?.problem ?? null;
  const name = serviceLabel(problem?.service ?? service);
  const why = describeReason(problem?.reason ?? reason);
  const env = problem?.environment ?? environment;
  const upstream = problem?.upstream_status ?? upstreamStatus;
  const code = problem?.upstream_code ?? upstreamCode ?? null;
  const known = describeUpstreamCode(code);

  return (
    <div className="panel-unavailable" role={write ? 'alert' : 'status'} data-panel="unavailable">
      <div className="panel-unavailable-title t-heading">
        {known ? `${name}: ${known.title}` : `${name} unavailable (503)`}
      </div>
      <div className="t-body">
        {known
          ? known.message
          : <>{problem?.title ?? detail ?? `${name} could not be reached.`}{why && <> — {why}.</>}</>}
      </div>
      <div className="t-caption muted panel-unavailable-meta">
        {env && <span>environment <span className="mono">{env}</span></span>}
        {(problem?.reason ?? reason) && <span>reason <span className="mono">{problem?.reason ?? reason}</span></span>}
        {typeof upstream === 'number' && <span>upstream status <span className="mono">{upstream}</span></span>}
        {code && <span>code <span className="mono">{code}</span></span>}
      </div>
      <div className="t-caption muted">
        {write
          ? 'The service did not confirm the change. Nothing was retried — check the current state before trying again.'
          : known && !known.outage
            ? 'The service is up; it has nothing for this tenant yet.'
            : 'This is live data: nothing cached or estimated is shown in its place. Retry when the service is back.'}
      </div>
    </div>
  );
}

/** A code chip for an UPPER_SNAKE application code, shown verbatim. */
export function CodeChip({ code }: { code: string }) {
  return <span className="code-chip mono">{code}</span>;
}

/** The details a problem body can carry beyond its title: framework field errors, pack errors. */
function ProblemDetails({ error }: { error: ApiError }) {
  const problem = error.problem;
  const fieldErrors = problem?.errors ? Object.entries(problem.errors) : [];
  const dataList = Array.isArray(problem?.data) ? (problem!.data as unknown[]).filter((d) => typeof d === 'string') as string[] : [];
  const jobId =
    problem?.data && typeof problem.data === 'object' && !Array.isArray(problem.data) && 'job_id' in problem.data
      ? String((problem.data as { job_id: unknown }).job_id)
      : null;

  if (fieldErrors.length === 0 && dataList.length === 0 && !jobId) return null;

  return (
    <ul className="problem-list t-caption">
      {fieldErrors.map(([field, messages]) => (
        <li key={field}><span className="mono">{field}</span>: {messages.join(' ')}</li>
      ))}
      {dataList.map((item) => <li key={item}>{item}</li>)}
      {jobId && <li>existing job <span className="mono">{jobId}</span></li>}
    </ul>
  );
}

/** A refused / failed call (not a 503): status, the server's code verbatim, and its message. */
export function ProblemCallout({ error, title }: { error: unknown; title?: string }) {
  if (!(error instanceof ApiError)) {
    return (
      <div className="callout callout-error" role="alert">
        <span className="callout-icon" aria-hidden="true">✕</span>
        <div className="t-body">
          <strong className="t-heading" style={{ display: 'block' }}>{title ?? 'Something went wrong'}</strong>
          {error instanceof Error ? error.message : 'Unexpected error.'}
        </div>
      </div>
    );
  }

  const forbidden = error.status === 403;
  // The sentence first, in words; the server's own message and code stay beside it for whoever investigates.
  const plain = plainError(error.code);
  return (
    <div className={`callout ${forbidden ? 'callout-warning' : 'callout-error'}`} role="alert">
      <span className="callout-icon" aria-hidden="true">{forbidden ? '!' : '✕'}</span>
      <div className="t-body" style={{ minWidth: 0 }}>
        <strong className="t-heading" style={{ display: 'block' }}>
          {title ?? refusalTitle(error.status)}
        </strong>
        {plain && <div>{plain}</div>}
        <div className={`problem-line${plain ? ' t-caption muted' : ''}`}>
          {error.code && <CodeChip code={error.code} />}
          <span>{forbidden && !error.problem?.title ? 'Your role does not include the permission this needs.' : error.message}</span>
        </div>
        <ProblemDetails error={error} />
      </div>
    </div>
  );
}

/**
 * The error of a failed MUTATION, next to the control that caused it. A 503 is the unavailable panel in
 * its write form (the outcome is unknown); anything else shows the server's code verbatim —
 * `422 REASON_REQUIRED` and `400 ADMIN_ACTOR_REQUIRED` included (CP5).
 */
export function MutationError({ error, service }: { error: unknown; service?: string }) {
  if (!error) return null;
  if (error instanceof ApiError && error.isUnavailable) {
    return <UnavailablePanel error={error} service={service} write />;
  }
  return <ProblemCallout error={error} />;
}

interface QueryLike<T> {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: T | undefined;
}

interface QueryStateProps<T> {
  query: QueryLike<T>;
  /** For the 503 panel's label when the body does not name the service. */
  service?: string;
  children: (data: T) => ReactNode;
  /** Rendered instead of the generic callout on a 404 (e.g. "no such tenant on this environment"). */
  notFound?: ReactNode;
}

/**
 * Renders a query's state in one consistent way: spinner → error → data.
 *
 * The ERROR is checked before the data on purpose. When a live refetch fails, react-query keeps the last
 * successful answer in `data`; rendering it beside a failed live call is exactly the stale money figure
 * CP2 forbids, so a failed query shows its panel and nothing else.
 */
export function QueryState<T>({ query, service, children, notFound }: QueryStateProps<T>) {
  if (query.isError) {
    const error = query.error;
    if (error instanceof ApiError && error.isUnavailable) return <UnavailablePanel error={error} service={service} />;
    if (notFound && error instanceof ApiError && error.status === 404) return <>{notFound}</>;
    return <ProblemCallout error={error} />;
  }

  if (query.isPending || query.data === undefined) {
    return (
      <div className="empty-state" role="status" aria-label="Loading">
        <span className="spinner" />
      </div>
    );
  }

  return <>{children(query.data)}</>;
}
